import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { wsClient } from '../../services/websocket-client.js';
import { ProjectStateController } from '../../services/project-state.js';
import type { TerminalCreatedMessage } from '../../types/project.js';
import '../breakdown/cp-breakdown-terminal.js';

@customElement('cp-team-terminal-modal')
export class CpTeamTerminalModal extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Boolean }) open = false;

  @state() private _sessionId = '';
  @state() private _sessionError = '';

  private _projectState = new ProjectStateController(this);
  private _wsHandler = this._onWsMessage.bind(this);
  private _sessionRequested = false;

  connectedCallback(): void {
    super.connectedCallback();
    wsClient.addEventListener('ws-message', this._wsHandler as EventListener);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    wsClient.removeEventListener('ws-message', this._wsHandler as EventListener);
    this._destroySession();
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('open')) {
      if (this.open && !this._sessionRequested) {
        this._createSession();
      } else if (!this.open) {
        this._destroySession();
        this._sessionRequested = false;
        this._sessionId = '';
        this._sessionError = '';
      }
    }

    // Retry session creation when projects load after initial attempt
    if (this.open && !this._sessionId && !this._sessionRequested && this._projectState.projects.length > 0) {
      this._createSession();
    }
  }

  private _createSession(): void {
    const projectId = this._projectState.projects[0]?.id;
    if (!projectId) {
      this._sessionError = 'Kein Projekt gefunden';
      return;
    }

    this._sessionRequested = true;
    wsClient.send({
      type: 'terminal:create-workflow',
      payload: { projectId, level: 'add-team-member' },
      timestamp: new Date().toISOString(),
    });
  }

  private _destroySession(): void {
    if (this._sessionId) {
      wsClient.send({
        type: 'terminal:close',
        payload: { sessionId: this._sessionId },
        timestamp: new Date().toISOString(),
      });
      this._sessionId = '';
    }
  }

  private _onWsMessage(e: CustomEvent): void {
    const msg = e.detail as { type: string; payload: unknown };

    if (msg.type === 'terminal:created') {
      const payload = msg.payload as TerminalCreatedMessage;
      if (payload.sessionId.startsWith('workflow-add-team-member')) {
        this._sessionId = payload.sessionId;
      }
    } else if (msg.type === 'terminal:workflow-complete') {
      const payload = msg.payload as { sessionId: string; level: string };
      if (payload.sessionId === this._sessionId) {
        // Command completed successfully - close modal and signal reload
        this._close(true);
      }
    } else if (msg.type === 'terminal:session-end') {
      const payload = msg.payload as { sessionId: string; exitCode: number };
      if (payload.sessionId === this._sessionId) {
        this._sessionId = '';
      }
    } else if (msg.type === 'terminal:exited') {
      const payload = msg.payload as { sessionId: string };
      if (payload.sessionId === this._sessionId) {
        this._sessionId = '';
      }
    }
  }

  private _close(completed = false): void {
    this._destroySession();
    this.dispatchEvent(new CustomEvent('terminal-closed', {
      detail: { completed },
      bubbles: true,
      composed: true,
    }));
  }

  render() {
    if (!this.open) return nothing;

    return html`
      <div class="cp-team-terminal-modal__overlay">
        <div class="cp-team-terminal-modal__dialog">
          <div class="cp-team-terminal-modal__header">
            <h2 class="cp-team-terminal-modal__heading">Teammitglied hinzufuegen</h2>
            <button class="cp-team-terminal-modal__close" @click=${() => this._close()}>&times;</button>
          </div>
          <div class="cp-team-terminal-modal__body">
            ${this._sessionError ? html`
              <div style="padding: var(--spacing-lg); color: var(--color-accent-error);">${this._sessionError}</div>
            ` : html`
              <cp-breakdown-terminal
                .sessionId=${this._sessionId}
                .breakdownLevel=${'add-team-member'}
              ></cp-breakdown-terminal>
            `}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-team-terminal-modal': CpTeamTerminalModal;
  }
}
